import { useState } from "react";
import { ArrowRight, Check } from "lucide-react";
import { AsYouType } from "libphonenumber-js";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { sampleContactPayload } from "@/lib/sampleContact";
import { PrivacyLink } from "@/components/privacy/PrivacyLink";

const INITIAL_FORM = {
  name: "",
  email: "",
  phone: "",
  feedback: "",
  source: "five-day-sample-feedback",
};

const inputClass = "w-full border-0 border-b border-[#0f0f10]/25 bg-transparent px-0 py-3 text-base text-[#0f0f10] outline-none placeholder:text-[#0f0f10]/30 focus:border-[#0f0f10] focus:ring-0";

const formatPhoneInput = (value) => {
  const international = value.trimStart().startsWith("+");
  const digits = value.replace(/\D/g, "").slice(0, 15);
  if (!digits) return international ? "+" : "";
  return new AsYouType(international ? undefined : "US").input(`${international ? "+" : ""}${digits}`);
};

export const FeedbackForm = () => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const update = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: name === "phone" ? formatPhoneInput(value) : value }));
    setError("");
  };

  const submit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    setError("");
    let contact;
    try { contact = sampleContactPayload(form); }
    catch (err) {
      setError(err.message);
      return;
    }
    setSubmitting(true);
    try {
      await api.sampleFeedback({ name: form.name.trim(), feedback: form.feedback.trim(), source: form.source, ...contact });
      setSubmitted(true);
      toast.success("Thank you for sharing your feedback.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(err.response?.status === 409 && typeof detail === "string" ? detail : "We couldn’t save your feedback. Please check your details and try again.");
      toast.error("Unable to send your feedback.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section className="ph-no-capture ph-mask flex min-h-[620px] flex-col justify-center bg-[#faf9f5] px-6 py-20 md:px-12 lg:min-h-screen lg:px-20" data-testid="feedback-success">
        <span className="flex h-11 w-11 items-center justify-center border border-[#0f0f10] text-[#0f0f10]" data-testid="feedback-success-icon"><Check size={18} /></span>
        <div className="mt-9 label !text-[9px] text-[color:var(--ink-3)]">Feedback received</div>
        <h2 className="mt-5 max-w-xl font-display text-5xl leading-[0.98]" data-testid="feedback-success-title">
          Thank <span className="editorial font-normal">you.</span>
        </h2>
        <p className="mt-6 max-w-md text-base leading-relaxed text-[color:var(--ink-2)]" data-testid="feedback-success-message">
          Your honest read helps us make Vitura better. We review every response and will only use your contact details if we need to follow up about what you shared.
        </p>
        <a href="/" className="mt-10 inline-flex w-fit items-center gap-2 border-b border-[#0f0f10] pb-1 label !text-[9px] transition-opacity duration-200 hover:opacity-55" data-testid="feedback-success-home-link">
          Return to Vitura <ArrowRight size={13} />
        </a>
      </section>
    );
  }

  return (
    <section className="ph-no-capture ph-mask bg-[#faf9f5] px-6 py-16 text-[#0f0f10] md:px-12 md:py-20 lg:min-h-screen lg:px-20 lg:py-24" data-testid="feedback-form-panel">
      <div className="mx-auto max-w-2xl lg:mx-0">
        <div className="label !text-[9px] text-[color:var(--ink-3)]">Two minutes / No wrong answers</div>
        <h2 className="mt-5 font-display text-4xl leading-[1.02] sm:text-5xl" data-testid="feedback-form-title">
          Tell us how the<br /><span className="editorial font-normal">five days felt.</span>
        </h2>

        <form onSubmit={submit} className="mt-12" data-testid="feedback-form">
          <p id="feedback-contact-requirement" className="mb-5 text-xs leading-relaxed text-[color:var(--ink-3)]" data-testid="feedback-contact-requirement">Provide an email address or phone number—either one is enough.</p>
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
            <label className="block text-xs text-[color:var(--ink-3)] sm:col-span-2">Name
              <input
                required
                autoComplete="name"
                name="name"
                value={form.name}
                onChange={update}
                className={inputClass}
                data-testid="feedback-name-input"
              />
            </label>
            <label className="block text-xs text-[color:var(--ink-3)]">Email
              <input
                required={!form.phone.trim()}
                type="email"
                autoComplete="email"
                name="email"
                aria-describedby="feedback-contact-requirement"
                value={form.email}
                onChange={update}
                className={inputClass}
                data-testid="feedback-email-input"
              />
            </label>
            <label className="block text-xs text-[color:var(--ink-3)]">Phone number
              <input
                required={!form.email.trim()}
                type="tel"
                autoComplete="tel"
                inputMode="tel"
                name="phone"
                aria-describedby="feedback-contact-requirement"
                minLength={7}
                maxLength={28}
                value={form.phone}
                onChange={update}
                placeholder="(305) 555-0123"
                className={inputClass}
                data-testid="feedback-phone-input"
              />
              <span className="mt-2 block text-[10px] leading-relaxed text-[color:var(--ink-3)]" data-testid="feedback-phone-format-help">Outside the U.S., start with + and your country code.</span>
            </label>
          </div>

          <p className="mt-5 max-w-xl text-xs leading-relaxed text-[color:var(--ink-3)]" data-testid="feedback-privacy-note">
            Your contact details help us match your sample record and follow up about your feedback. This does not enroll you in marketing. Read our <PrivacyLink testId="feedback-form-privacy-link" newTab /> for information about providers, website analytics, and your choices.
          </p>

          <div className="mt-12">
            <label htmlFor="feedback-response" className="block font-display text-2xl">Your honest read</label>
            <p className="mt-3 max-w-xl editorial text-xl leading-snug text-[color:var(--ink-2)]" id="feedback-guidance" data-testid="feedback-guidance">
              We’d love to hear what you felt, when you felt it, how it tasted, what you’d change—or anything else you’d like to share.
            </p>
            <textarea
              id="feedback-response"
              required
              minLength={20}
              maxLength={6000}
              rows={9}
              name="feedback"
              value={form.feedback}
              onChange={update}
              aria-describedby="feedback-guidance feedback-count"
              placeholder="Start wherever it makes sense…"
              className="mt-7 w-full resize-y border border-[#0f0f10]/25 bg-transparent p-4 text-base leading-relaxed text-[#0f0f10] outline-none placeholder:text-[#0f0f10]/28 focus:border-[#0f0f10] focus:ring-0"
              data-testid="feedback-response-input"
            />
            <div className="mt-2 flex items-center justify-between gap-4 text-[10px] text-[color:var(--ink-3)]">
              <span>Minimum 20 characters</span>
              <span id="feedback-count" className="font-mono tabular-nums" data-testid="feedback-character-count">{form.feedback.length} / 6000</span>
            </div>
          </div>

          {error && <p className="mt-6 border border-red-700/30 p-3 text-sm text-red-800" role="alert" data-testid="feedback-form-error">{error}</p>}

          <button
            type="submit"
            disabled={submitting}
            className="mt-9 inline-flex min-h-12 w-full items-center justify-between bg-[#0f0f10] px-6 py-3 label !text-[9px] !text-[#faf9f5] transition-colors duration-200 hover:bg-[#a8c69f] hover:!text-[#0f0f10] disabled:cursor-wait disabled:opacity-50 sm:w-auto sm:min-w-[250px]"
            data-testid="feedback-submit-button"
          >
            {submitting ? "Sending…" : "Share my feedback"} <ArrowRight size={14} />
          </button>
        </form>
      </div>
    </section>
  );
};